import React, { useState } from 'react';
import { Typography, Box, Dialog, DialogContent, Grid } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { fr } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { formatDateOnly } from '@/utils/date';

interface Props {
  value: string | null;
  onChange: (newDate: string) => void;
  isOutdated?: boolean;
}

const StartDateSelector = ({ value, onChange, isOutdated = false }: Props) => {
  const { t } = useTranslation('habitation');
  const [open, setOpen] = useState(false);

  const rawParsed = value ? new Date(value) : null;
  const parsedValue = rawParsed && !isNaN(rawParsed.getTime()) ? rawParsed : null;
  // Quand la date est périmée, on ne la passe pas comme valeur initiale au DatePicker
  // (sinon `disablePast` empêcherait l'ouverture sur une date sélectionnable).
  const datePickerValue = isOutdated ? null : parsedValue;

  const handleDateChange = (newDate: Date | null) => {
    if (!newDate) return;

    const now = new Date();
    const selected = new Date(newDate);

    const isToday =
      selected.getFullYear() === now.getFullYear() &&
      selected.getMonth() === now.getMonth() &&
      selected.getDate() === now.getDate();

    let finalISOString: string;

    if (isToday) {
      // Garder l'heure actuelle
      finalISOString = now.toISOString();
    } else {
      // Créer une date UTC à minuit (00:00:00.000Z)
      const utcDate = new Date(Date.UTC(
        selected.getFullYear(),
        selected.getMonth(),
        selected.getDate(),
        0, 0, 0, 0
      ));
      finalISOString = utcDate.toISOString();
    }

    onChange(finalISOString); // string conforme à ton slice Redux
    setOpen(false);
  };

  return (
    <Box display="flex" flexDirection="column" textAlign="center" alignItems="center" gap={1}>
      <Grid container justifyContent="center" alignItems="center" mb={isOutdated ? 0 : 2}>
        <Grid item xs={12} sm={12} spacing="3" display="contents">
          <Typography variant="h3" fontWeight={500}>
            {t('step5.start_date')}
          </Typography>
          <Typography
            variant="h3"
            sx={{
              textDecoration: 'underline',
              cursor: 'pointer',
              color: isOutdated ? '#d32f2f' : 'blue',
              marginLeft: '0.5em',
            }}
            onClick={() => setOpen(true)}
          >
            {parsedValue
              ? formatDateOnly(value)
              : 'XX/XX/XXXX'}
          </Typography>
          <Dialog open={open} onClose={() => setOpen(false)}>
            <DialogContent>
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={fr}
              >
                <DatePicker
                  label={t('step5.start_date')}
                  value={datePickerValue}
                  onChange={handleDateChange}
                  disablePast
                  slotProps={{
                    textField: { fullWidth: true },
                  }}
                />
              </LocalizationProvider>
            </DialogContent>
          </Dialog>
        </Grid>
      </Grid>
      {isOutdated && (
        <Typography variant="body2" sx={{ color: '#d32f2f', mb: 2, fontWeight: 500 }}>
          {t('common:errors.start_date_outdated')}
        </Typography>
      )}
    </Box>
  );
};

export default StartDateSelector;
